import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  useGetUnreadNotificationCount,
  useGetStickyNotes,
  useAttendanceManager,
  useIsDepartmentHead,
  useGetUpcomingMeetingCount,
} from "../../api/hooks";
import { syncTimer, updateTimer } from "../../store/slice/timerSlice";
import { useRouteAccess } from "../../hooks/useRouteAccess";
import { usePermissions } from "../../hooks/usePermissions";
import { MdDashboard } from "react-icons/md";
import logo from "../../assets/icons/logo.svg";
import { SIDE_MENU } from "../../constants";
import { getMyPerformance } from "../../api/service";
import { useIsReporter } from "../../api/hooks/dashboard";
import socketService from "../../services/socketService";
import { toast } from "react-hot-toast";
import { Zap } from "lucide-react";

// Import components
import SearchBar from "./components/SearchBar";
import AttendanceStatus from "./components/AttendanceStatus";
import ActionButtons from "./components/ActionButtons";
import AttendanceModal from "./components/AttendanceModal";
import BreakModal from "./components/BreakModal";
import MobileSidebar from "./components/MobileSidebar";
import UserProfile from "./components/UserProfile";
import NotificationBar from "../notificationBar";
import AttendanceRequestsDrawer from "../../features/attendance/components/AttendanceRequestsDrawer";
import { usePendingCorrectionRequests } from "../../features/attendance/hooks/useAttendanceMutations";
import { signOutOfApp } from "../../pwa/closeGuard";

const DashboardHeader = () => {
  // State management
  const [isNotifyMenuOpen, setNotifyMenuOpen] = useState(false);
  const [isAttendanceRequestsOpen, setAttendanceRequestsOpen] = useState(false);
  const [isAttendanceMenuOpen, setAttendanceMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [shiftElapsedTime, setShiftElapsedTime] = useState(0);
  const [currentScore, setCurrentScore] = useState(0);

  // Hooks
  const { user, companyId } = useAuth();
  const effectiveCompanyId = companyId || user?.company;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userPosition } = useRouteAccess();
  const { hasPermission, hasAdminDashboardAccess } = usePermissions();
  const { isDepartmentHead } = useIsDepartmentHead(effectiveCompanyId, !!user);
  const { isReporter } = useIsReporter(!!user && user.role === "employee");

  // Data fetching
  const { data: unreadData } = useGetUnreadNotificationCount();
  const { data: stickyNotes = [] } = useGetStickyNotes();
  const { data: upcomingMeetingsData } = useGetUpcomingMeetingCount();
  const unreadCount = unreadData?.count || 0;
  const stickyNotesCount = stickyNotes.length || 0;
  const upcomingMeetingCount = upcomingMeetingsData?.count || 0;

  // Attendance management
  const {
    currentStatus,
    todayRecords,
    isShiftActive,
    isOnBreak,
    shiftStartTime,
    statusLoading,
    handleClockIn,
    handleClockOut,
    handleStartBreak,
    handleEndBreak,
    isClockingIn,
    isClockingOut,
    isStartingBreak,
    isEndingBreak,
    clockInError,
    clockOutError,
    endBreakError,
    isEndShiftBlocked,
    pendingTasksWithoutReasonCount,
  } = useAttendanceManager();

  // Prevent multiple modal opens
  const handleAttendanceClick = () => {
    if (!isAttendanceMenuOpen && !isClockingIn) {
      setAttendanceMenuOpen(true);
    }
  };

  // Timer state from Redux
  const { remainingTime, isRunning } = useSelector((state) => state.timer);

  // Check if user has admin dashboard access permission
  // IMPORTANT: Company admins should NOT see this option - only non-admin users with permission
  const isCompanyAdmin = user?.role === "company-admin";
  const canReviewAttendanceRequests =
    isCompanyAdmin || hasPermission("attendance", "edit");
  const { data: correctionRequestsData, isLoading: correctionRequestsLoading } =
    usePendingCorrectionRequests(canReviewAttendanceRequests);
  const attendanceRequestCount = correctionRequestsData?.count || 0;
  const attendanceRequests = correctionRequestsData?.requests || [];
  const showHeaderActions = isCompanyAdmin || isShiftActive;
  const canAccessAdminDashboard = !isCompanyAdmin && hasAdminDashboardAccess();

  // Build sidebar menu items - only add Company Dashboard if user has permission (but NOT for admins)
  const sidebarMenuItems = SIDE_MENU.map((item) => {
    // If the item is "Dashboard", check if sub-items should be shown
    if (item.routeKey === "dashboard") {
      // ONLY Company Admin sees all sub-items under the main "Dashboard"
        if (isCompanyAdmin) {
          const hasDepartmentDashboard = item.children?.some(
            (child) => child.routeKey === "department-dashboard"
          );

          if (!hasDepartmentDashboard) {
            return {
              ...item,
              children: [
                ...(item.children || []),
                {
                  id: 107,
                  title: "Department Dashboard",
                  path: "/department-dashboard",
                  routeKey: "department-dashboard",
                },
              ],
            };
          }

          return item;
        }

      // For others, check if they have specific dashboard permissions (like lead-dashboard)
      const allowedRoutes = userPosition?.allowedRoutes || [];
      const hasDashboardChildren = item.children?.some(
        (child) =>
          child.routeKey !== "dashboard" && allowedRoutes.includes(child.routeKey)
      );

      if (hasDashboardChildren) {
        // Return dashboard with only allowed children
        const allowedChildren = item.children.filter(
          (child) =>
            child.routeKey === "dashboard" ||
            allowedRoutes.includes(child.routeKey)
        );

        if (isDepartmentHead) {
          allowedChildren.push({
            id: 107,
            title: "Department Dashboard",
            path: "/department-dashboard",
            routeKey: "department-dashboard",
          });
        }

        return { ...item, children: allowedChildren };
      }

      if (isDepartmentHead) {
        return {
          ...item,
          children: [
            {
              id: 101,
              title: "Main Dashboard",
              path: "/",
              routeKey: "dashboard",
            },
            {
              id: 107,
              title: "Department Dashboard",
              path: "/department-dashboard",
              routeKey: "department-dashboard",
            },
          ],
        };
      }

      // If no specific dashboard permissions, return a flat "Dashboard" link
      const { children, ...rest } = item;
      return rest;
    }
    return item;
  });

  // IMPORTANT: Only add Company Dashboard menu item if user has the permission AND is NOT a company admin
  if (canAccessAdminDashboard) {
    // Insert Company Dashboard after Dashboard
    const dashboardIndex = sidebarMenuItems.findIndex(
      (item) => item.routeKey === "dashboard"
    );
    if (dashboardIndex !== -1) {
      sidebarMenuItems.splice(dashboardIndex + 1, 0, {
        id: 13,
        title: "Company Dashboard",
        icon: MdDashboard,
        path: "/company-dashboard",
        routeKey: "company-dashboard",
      });
    }
  }

  // Reporters get their own dashboard entry (a per-project assignment, so it
  // is not tied to the position's allowed routes)
  if (isReporter || isCompanyAdmin) {
    const dashboardIndex = sidebarMenuItems.findIndex(
      (item) => item.routeKey === "dashboard"
    );
    if (dashboardIndex !== -1) {
      sidebarMenuItems.splice(dashboardIndex + 1, 0, {
        id: 14,
        title: "Reporter Dashboard",
        icon: MdDashboard,
        path: "/reporter-dashboard",
        routeKey: "reporter-dashboard",
      });
    }
  }

  // Filter sidebar menu based on user permissions
  const filteredSidebar = sidebarMenuItems.filter((item) => {
    // Company admins have full access to all menu items EXCEPT Company Dashboard
    if (isCompanyAdmin) {
      // Hide Company Dashboard for admins
      if (item.routeKey === "company-dashboard") {
        return false;
      }
      return true;
    }

    if (item.title === "My Calls" && user?.role === "client") {
      return false;
    }

    // Dashboard, Board, and Settings are always accessible to everyone
    if (
      item.routeKey === "dashboard" ||
      item.routeKey === "board" ||
      item.routeKey === "settings" ||
      item.routeKey === "tickets"
    ) {
      return true;
    }

    if (item.routeKey === "reporter-dashboard") {
      return true;
    }

    // Company Dashboard is ONLY accessible if user has the permission (and is NOT an admin)
    if (item.routeKey === "company-dashboard") {
      return canAccessAdminDashboard;
    }

    // For other menu items, check if the routeKey is in their allowed routes
    const allowedRoutes = userPosition?.allowedRoutes || [];
    return allowedRoutes.includes(item.routeKey);
  });

  // Effects
  useEffect(() => {
    dispatch(syncTimer());
  }, [dispatch]);

  useEffect(() => {
    let interval;
    if (isRunning && remainingTime > 0) {
      interval = setInterval(() => {
        dispatch(updateTimer());
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, remainingTime, dispatch]);

  useEffect(() => {
    if (!isShiftActive || !shiftStartTime) {
      setShiftElapsedTime(0);
      return undefined;
    }
    const tick = () => {
      setShiftElapsedTime(Math.max(0, Math.floor((Date.now() - shiftStartTime.getTime()) / 1000)));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isShiftActive, shiftStartTime]);

  useEffect(() => {
    if (isCompanyAdmin) return;

    fetchScore();

    const handlePointsAwarded = (data) => {
      console.log("[Gamification Debug] Header received points_awarded socket event:", data);
      
      // If the backend provided the new score directly, update instantly!
      if (data.newTotalScore !== undefined) {
        setCurrentScore(data.newTotalScore);
      } else {
        // Fallback: Add a small delay to ensure backend has completed all database transactions
        setTimeout(() => {
          fetchScore();
        }, 500);
      }
      
      // Determine style based on if it's a penalty or bonus
      if (data.title?.includes("Delayed") || data.message?.includes("-")) {
        toast.error(
          <div>
            <strong>{data.title}</strong>
            <p className="text-sm mt-1">{data.message}</p>
          </div>
        );
      } else {
        toast.success(
          <div>
            <strong>{data.title}</strong>
            <p className="text-sm mt-1">{data.message}</p>
          </div>
        );
      }
    };

    socketService.onPointsAwarded(handlePointsAwarded);

    return () => {
      socketService.offPointsAwarded(handlePointsAwarded);
    };
  }, []);

  const fetchScore = async () => {
    try {
      const res = await getMyPerformance("monthly");
      if (res.success && res.performance) {
        setCurrentScore(res.performance.totalScore || 0);
      }
    } catch (err) {
      console.error("Failed to fetch score", err);
    }
  };

  // Utility functions
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleLogout = () => {
    signOutOfApp("/auth/signin");
  };

  const endShift = async () => {
    if (isClockingOut) return;

    try {
      const location = await import("../../utils/locationUtils").then((utils) =>
        utils.getUserLocation()
      );
      const workDescription = "Completed daily tasks";

      const result = await handleClockOut(location, workDescription);
      return result;
    } catch (error) {
      console.error("Failed to end shift:", error);
      throw error;
    }
  };

  return (
    <div className="bg-white lg:bg-transparent rounded-xl py-1 md:py-0 flex items-center gap-2 w-full min-w-0">
      {/* Left: mobile menu */}
      <button
        type="button"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="md:hidden shrink-0 flex items-center justify-center w-11 h-11 rounded-xl"
        aria-label="Open menu"
      >
        <img src={logo} alt="" className="w-9 h-9" />
      </button>

      {/* Center: search (desktop) */}
      <div className="min-w-0 flex-1 hidden lg:block">
        <SearchBar accessiblePages={filteredSidebar} />
      </div>

      {/* Page-provided header controls (e.g. dashboard month filter) */}
      <div id="header-date-slot" className="shrink-0 empty:hidden" />

      {/* Right: attendance + actions + profile */}
      <div className="ml-auto flex items-center gap-1.5 sm:gap-2 shrink-0">
        <AttendanceStatus
          isShiftActive={isShiftActive}
          isOnBreak={isOnBreak}
          shiftElapsedTime={shiftElapsedTime}
          breaks={currentStatus?.breaks}
          todayRecords={todayRecords}
          shiftStartTime={shiftStartTime}
          isClockingOut={isClockingOut}
          clockOutError={clockOutError}
          onEndShift={endShift}
          onStartBreak={handleStartBreak}
          isStartingBreak={isStartingBreak}
          isEndShiftBlocked={isEndShiftBlocked}
          pendingTasksWithoutReasonCount={pendingTasksWithoutReasonCount}
        />

        <ActionButtons
          isShiftActive={isShiftActive}
          isOnBreak={isOnBreak}
          statusLoading={statusLoading}
          isClockingIn={isClockingIn}
          onAttendanceClick={handleAttendanceClick}
          onNotifyClick={() => setNotifyMenuOpen(true)}
          onAttendanceRequestsClick={() => setAttendanceRequestsOpen(true)}
          unreadCount={unreadCount}
          stickyNotesCount={stickyNotesCount}
          upcomingMeetingCount={upcomingMeetingCount}
          attendanceRequestCount={attendanceRequestCount}
          showAttendanceRequests={canReviewAttendanceRequests}
          remainingTime={remainingTime}
          isRunning={isRunning}
          formatTime={formatTime}
        />

        {!isCompanyAdmin && (
          <div
            onClick={() => navigate(`/my-points`)}
            className="hidden md:flex items-center gap-1.5 px-3 h-12 bg-white rounded-[14px] hover:bg-gray-50 transition-colors cursor-pointer"
            title="Your Current Score"
          >
            <Zap className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-semibold text-gray-700">
              {currentScore}
            </span>
          </div>
        )}

        <UserProfile user={user} />
      </div>

      {/* Notification Bar */}
      {isNotifyMenuOpen && (
        <NotificationBar setNotifyMenuOpen={setNotifyMenuOpen} />
      )}

      {canReviewAttendanceRequests && (
        <AttendanceRequestsDrawer
          isOpen={isAttendanceRequestsOpen}
          onClose={() => setAttendanceRequestsOpen(false)}
          requests={attendanceRequests}
          isLoading={correctionRequestsLoading}
        />
      )}

      {/* Attendance Modal */}
      <AttendanceModal
        isOpen={isAttendanceMenuOpen}
        onClose={() => setAttendanceMenuOpen(false)}
        user={user}
        isClockingIn={isClockingIn}
        isProcessingAttendance={false}
        clockInError={clockInError}
        onClockIn={handleClockIn}
      />

      <BreakModal
        isOpen={isOnBreak}
        breaks={currentStatus?.breaks}
        isEndingBreak={isEndingBreak}
        endBreakError={endBreakError}
        onEndBreak={handleEndBreak}
      />

      {/* Mobile Sidebar */}
      <MobileSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        filteredSidebar={filteredSidebar}
        user={user}
        onLogout={handleLogout}
      />
    </div>
  );
};

export default DashboardHeader;
